import { useEffect, useMemo, useState } from "react";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { StringLimit } from "../../Utils";
import { fetchTemplateCategories, fetchTemplates } from "../../../api/templates";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { debounce } from "lodash";
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

export const TemplateDialog = (props) => {
  const { onChange, open, hasExistingContent = false } = props;
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const [isLoading, setIsLoading] = useState(true);

  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);

  const handleChange = (template, mode = "append") => {
    if (onChange) {
      onChange(template, { mode });
    }
  };

  const getTemplates = async () => {
    setIsLoading(true);
    await Promise.all([
      fetchTemplateCategories({ tree: true, include_templates: true }),
      fetchTemplates({ query: query, per_page: 100 }),
    ])
      .then((res) => {
        const [categoryResponse, templateResponse] = res;
        setIsLoading(false);
        setCategories(categoryResponse?.data?.data || []);
        setTemplates(templateResponse?.data?.data || []);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  const debouncedAction = useMemo(
    () =>
      debounce(() => {
        getTemplates();
      }, 300),
    [query]
  );

  useEffect(() => {
    if (open) {
      debouncedAction();
    } else {
      setSelectedCategory(null);
      setPreviewTemplate(null);
      setQuery("");
    }
    return () => {
      debouncedAction.cancel();
    };
  }, [query, open]);

  const categoryTemplates = selectedCategory?.templates || [];
  const visibleTemplates = selectedCategory
    ? filterTemplates(categoryTemplates, query)
    : filterTemplates(templates, query);

  return (
    <PMDialog {...props} title="Template" size="4xl">
      {isLoading ? (
        <ResourceLoading />
      ) : (
        <>
          {!previewTemplate && (
            <div>
              <PMField
                label={t("templates.search_placeholder")}
                onChange={(e) => setQuery(e?.target?.value)}
                value={query}
              />
            </div>
          )}

          {previewTemplate ? (
            <TemplatePreview
              template={previewTemplate}
              hasExistingContent={hasExistingContent}
              onBack={() => setPreviewTemplate(null)}
              onAppend={() => handleChange(previewTemplate, "append")}
              onReplace={() => handleChange(previewTemplate, "replace")}
            />
          ) : (
          <div className="grid gap-4 py-5 md:grid-cols-[260px_minmax(0,1fr)]">
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setSelectedCategory(null)}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm ${
                  !selectedCategory
                    ? "bg-slate-900 text-white"
                    : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <span>{t("templates.categories.all")}</span>
                <span>{templates.length}</span>
              </button>
              {categories.map((category) => (
                <CategoryOption
                  key={category.id}
                  category={category}
                  selectedCategory={selectedCategory}
                  onSelect={setSelectedCategory}
                />
              ))}
            </div>

            <div className="min-h-[260px] rounded-2xl border border-slate-200">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div className="text-sm font-semibold text-slate-900">
                  {selectedCategory?.name || t("templates.categories.all")}
                </div>
                {selectedCategory && (
                  <button
                    type="button"
                    onClick={() => setSelectedCategory(null)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                    {t("Back")}
                  </button>
                )}
              </div>
              <div className="divide-y divide-slate-100">
                {visibleTemplates.length < 1 ? (
                  <div className="px-4 py-8 text-center text-sm text-slate-500">
                    {t("templates.empty")}
                  </div>
                ) : (
                  visibleTemplates.map((template) => (
                    <button
                      type="button"
                      onClick={() => setPreviewTemplate(template)}
                      key={template.id}
                      className="block w-full px-4 py-3 text-left hover:bg-blue-50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900">
                            <StringLimit text={template.title} limit={50} />
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            {template.category?.name || t("templates.categories.uncategorized")}
                          </div>
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                          <EyeIcon className="h-3.5 w-3.5" />
                          {t("Preview")}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
          )}
        </>
      )}
    </PMDialog>
  );
};

function TemplatePreview({
  template,
  hasExistingContent,
  onBack,
  onAppend,
  onReplace,
}) {
  const { t } = useTranslation();

  return (
    <div className="py-5">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        {t("Back")}
      </button>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            {t("Preview")}
          </div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">
            {template.title}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {template.category?.name || t("templates.categories.uncategorized")}
          </p>
        </div>
        <div
          className="prose prose-sm max-h-[45vh] max-w-none overflow-y-auto px-5 py-5 text-slate-800"
          dangerouslySetInnerHTML={{ __html: template.content || "" }}
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
        {hasExistingContent && (
          <button
            type="button"
            onClick={onAppend}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
          >
            <DocumentDuplicateIcon className="h-4 w-4" />
            {t("templates.insert.append")}
          </button>
        )}
        <button
          type="button"
          onClick={onReplace}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
        >
          <ArrowPathIcon className="h-4 w-4" />
          {hasExistingContent
            ? t("templates.insert.replace")
            : t("templates.insert.use")}
        </button>
      </div>
    </div>
  );
}

function CategoryOption({ category, selectedCategory, onSelect, level = 0 }) {
  const active = selectedCategory?.id === category.id;
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => onSelect(category)}
        className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm ${
          active
            ? "bg-slate-900 text-white"
            : "bg-slate-50 text-slate-700 hover:bg-slate-100"
        }`}
        style={{ marginLeft: level * 12 }}
      >
        <span className="flex min-w-0 items-center gap-2">
          <FolderIcon className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{category.name}</span>
        </span>
        <span>{category.templates_count || 0}</span>
      </button>
      {category.children?.map((child) => (
        <CategoryOption
          key={child.id}
          category={child}
          selectedCategory={selectedCategory}
          onSelect={onSelect}
          level={level + 1}
        />
      ))}
    </div>
  );
}

function filterTemplates(templates = [], query = "") {
  if (!query?.trim()) return templates || [];
  const normalizedQuery = query.toLowerCase();
  return (templates || []).filter((template) =>
    `${template.title || ""} ${template.content || ""}`
      .toLowerCase()
      .includes(normalizedQuery)
  );
}
